import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { ReportTable, ReportColumn } from '@/components/ReportTable';
import { apiFetch } from '@/lib/api';
import { formatCents } from '@/lib/utils';
import DateRangePresets, {
  type Preset,
  boundsFor,
  rangeReady,
} from '@/components/DateRangePresets';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface SalesByItemRow {
  item: string;
  item_id: number | null;
  qty: number;
  avg_price: number;
  amount: number;
}

interface SalesByItemData {
  from: string;
  to: string;
  rows: SalesByItemRow[];
  total_amount: number;
  total_qty: number;
  count: number;
}

interface SalesByItemResponse {
  data: SalesByItemData;
}

// ─── Sort options ────────────────────────────────────────────────────────────────

const SORTS: { value: 'asc' | 'desc'; label: string }[] = [
  { value: 'desc', label: 'Highest $' },
  { value: 'asc', label: 'Lowest $' },
];

/** Qty formatter — thousands separators, up to 2 decimals (matches sq-ft fractions). */
function formatQty(value: number): string {
  return new Intl.NumberFormat('en-CA', { maximumFractionDigits: 2 }).format(value);
}

// ─── Columns ───────────────────────────────────────────────────────────────────

const COLUMNS: ReportColumn[] = [
  {
    key: 'item',
    header: 'Item',
    render: (value, row) => {
      const r = row as unknown as SalesByItemRow;
      const text = value == null ? '—' : String(value);
      if (r.item_id != null) {
        return (
          <Link
            to={`/items/${r.item_id}`}
            className="text-primary hover:underline underline-offset-4"
          >
            {text}
          </Link>
        );
      }
      return text;
    },
  },
  {
    key: 'qty',
    header: 'Qty Sold',
    align: 'right',
    render: (value) => (value == null ? '—' : formatQty(Number(value))),
  },
  { key: 'avg_price', header: 'Avg Price', money: true },
  { key: 'amount', header: 'Sales Amount', money: true },
];

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function SalesByItemReport() {
  const [preset, setPreset] = useState<Preset>('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [sort, setSort] = useState<'asc' | 'desc'>('desc');

  const { from, to } = useMemo(
    () => boundsFor(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['report', 'sales-by-item', from, to, sort],
    queryFn: () =>
      apiFetch<SalesByItemResponse>(
        `/api/reports/sales-by-item?from=${from}&to=${to}&sort=${sort}`,
      ),
    enabled: rangeReady(preset, customFrom, customTo),
  });

  const report = data?.data;
  const rows = (report?.rows ?? []) as unknown as Record<string, unknown>[];

  return (
    <div className="p-6">
      {/* Back link */}
      <Link
        to="/reports"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
      >
        <ChevronLeft className="h-3 w-3" />
        Reports
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Sales by Item</h1>
        <ExportButtons endpoint="/api/reports/sales-by-item" params={{ from, to, sort }} />
      </div>

      {/* Date-range presets */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <DateRangePresets
          preset={preset}
          onPresetChange={setPreset}
          customFrom={customFrom}
          onCustomFromChange={setCustomFrom}
          customTo={customTo}
          onCustomToChange={setCustomTo}
          idPrefix="sbi"
        />

        {/* Sort order */}
        <div className="flex items-center rounded-md border bg-muted/40 p-0.5 gap-0.5 ml-auto">
          {SORTS.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setSort(s.value)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                sort === s.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Loading / error */}
      {isLoading && <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>}
      {isError && (
        <p className="text-sm text-destructive py-10 text-center">
          {error instanceof Error ? error.message : 'Failed to load report'}
        </p>
      )}

      {report && (
        <>
          {/* Summary line */}
          <p className="text-sm text-muted-foreground mb-3">
            <span className="font-medium text-foreground">{report.count}</span> items ·{' '}
            <span className="font-medium text-foreground">Qty {formatQty(report.total_qty)}</span> ·{' '}
            <span className="font-medium text-foreground">
              Total {formatCents(report.total_amount)}
            </span>
          </p>

          {/* Line items */}
          <ReportTable columns={COLUMNS} rows={rows} />
        </>
      )}
    </div>
  );
}
